import './App.css';
import { useEffect, useState } from 'react';
import logo from './assets/logo.png';

const API_URL = process.env.REACT_APP_API_URL || 'https://gdpr-saas-1.onrender.com';
const THEME_STORAGE_KEY = 'checkwebrisk-theme';

function getInitialTheme() {
  const savedTheme = window.localStorage.getItem(THEME_STORAGE_KEY);

  if (savedTheme === 'dark' || savedTheme === 'light') {
    return savedTheme;
  }

  if (typeof window.matchMedia === 'function' && window.matchMedia('(prefers-color-scheme: dark)').matches) {
    return 'dark';
  }

  return 'light';
}

function App() {
  const [url, setUrl] = useState('');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [theme, setTheme] = useState(getInitialTheme);

  const riskClass = result?.riskLevel ? result.riskLevel.toLowerCase().replace(/\s+/g, '-') : '';
  const isDarkTheme = theme === 'dark';

  useEffect(() => {
    window.localStorage.setItem(THEME_STORAGE_KEY, theme);
    document.documentElement.setAttribute('data-theme', theme);
  }, [theme]);

  const handleThemeToggle = () => {
    setTheme((currentTheme) => currentTheme === 'dark' ? 'light' : 'dark');
  };

  const handleScan = async (e) => {
    e.preventDefault();
    if (!url.trim()) {
      setError('Please enter a URL');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      if (window.gtag) {
        window.gtag('event', 'scan_website', {
          event_category: 'engagement',
          event_label: url,
        });
      }
      const response = await fetch(`${API_URL}/scan`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ url }),
      });

      if (!response.ok) {
        const errorData = await response.json();

        throw new Error(
          errorData.details || errorData.error || `HTTP ${response.status}`
        );
      }

      const data = await response.json();
      setResult(data);
    } catch (err) {
      setError(err.message ?? 'Scan failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`App theme-${theme}`}>
      <button
        type="button"
        className="Theme-toggle"
        onClick={handleThemeToggle}
        aria-pressed={isDarkTheme}
        aria-label={isDarkTheme ? 'Switch to light mode' : 'Switch to dark mode'}
      >
        <span className="Theme-toggle-track" aria-hidden="true">
          <span className="Theme-toggle-thumb"></span>
        </span>
        {isDarkTheme ? 'Light mode' : 'Dark mode'}
      </button>
      <main className="App-main">
        <img src={logo} alt="CheckWebRisk Logo" className="Brand-logo" />
        <p>Enter a website URL to check the risk signals.</p>

        <form onSubmit={handleScan} className={`App-form ${loading ? 'is-scanning' : ''}`}>
          <input
            type="url"
            placeholder="https://example.com"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            disabled={loading}
            className="App-input"
          />
          <button type="submit" disabled={loading} className="App-button">
            {loading ? 'Scanning...' : 'Scan Website'}
          </button>
        </form>

        {loading && result && (
          <div className="scan-status" role="status" aria-live="polite">
            <span className="scan-spinner" aria-hidden="true"></span>
            Scanning latest URL...
          </div>
        )}

        {result && (
          <div className={`App-result ${loading ? 'result-updating' : ''}`}>
            {loading && (
              <div className="result-overlay" aria-hidden="true">
                <span className="scan-spinner"></span>
                Updating scan...
              </div>
            )}
            <h2>Scan Results</h2>
            <p><strong>URL:</strong> {result.url}</p>
            <div className={`risk-banner risk-${riskClass}`}>
              <span className="risk-marker" aria-hidden="true"></span>
              <strong>{result.riskLevel}</strong>
            </div>
            <p><strong>GDPR Compliance Score:</strong> <span className={`score score-${result.score}`}>{result.score}/100</span></p>
            <p className="trust-note">Based on GDPR best practices analysis.</p>
            <p><strong>Cookie Banner Detected:</strong> <span className={result.hasCookieBanner ? 'status-detected' : 'status-not-detected'}>{result.hasCookieBanner ? 'Yes' : 'No'}</span></p>
            <p><strong>Privacy Policy Detected:</strong> <span className={result.hasPrivacyPolicy ? 'status-detected' : 'status-not-detected'}>{result.hasPrivacyPolicy ? 'Yes' : 'No'}</span></p>

            {result.issues && result.issues.length > 0 && (
              <div className="issues">
                <h3>Issues Found</h3>
                <ul>
                  {result.issues.map((issue, idx) => (
                    <li key={idx} className={`issue issue-${issue.severity}`}>
                      <div className="issue-heading">
                        <span className="issue-marker" aria-hidden="true"></span>
                        <strong>{issue.severity.charAt(0).toUpperCase() + issue.severity.slice(1)}:</strong>
                        <span>{issue.message}</span>
                      </div>
                      <span className="issue-priority">{issue.priority}</span>
                      <p>{issue.recommendation}</p>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {result.trackers && result.trackers.length > 0 && (
              <div className="trackers">
                <h3>Trackers Found ({result.trackers.length})</h3>
                <ul>
                  {result.trackers.map((tracker, idx) => (
                    <li key={idx}>
                      <strong>{tracker.type}</strong>: {tracker.risk}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {(!result.trackers || result.trackers.length === 0) && (
              <p className="no-trackers">No tracking scripts detected ✓</p>
            )}
          </div>
        )}

        {error && <p className="App-error">{error}</p>}
      </main>
    </div>
  );
}

export default App;
